import React, { useState } from 'react';
import { Lock, Unlock, History, Loader2 } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';
import { destravarUc } from './rateioTravamentoActions';
import UcHistoricoPopover from './UcHistoricoPopover';

interface UcsTravadasCardProps {
  ucs: UcParaRateio[];
  onChanged: () => void;
}

const MOTIVO_BADGE: Record<string, string> = {
  excesso_saldo: 'Excesso de Saldo',
  manual: 'Manual',
};

const formatData = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

/**
 * UCs travadas (situacao = 'Pausada') ficam fora da grade principal do
 * Simulador (que soma 100%) e aparecem aqui, somente leitura, com "Destravar
 * UC" restrito a Gestor (Seção 9/11 da spec).
 */
const UcsTravadasCard: React.FC<UcsTravadasCardProps> = ({ ucs, onChanged }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';
  const [destravandoId, setDestravandoId] = useState<number | null>(null);
  const [historico, setHistorico] = useState<{ uc: UcParaRateio; anchor: DOMRect } | null>(null);

  if (ucs.length === 0) return null;

  const handleDestravar = async (uc: UcParaRateio) => {
    setDestravandoId(uc.id);
    try {
      await destravarUc(uc.id);
      addToast(`UC ${uc.numero_uc} destravada.`, 'success');
      onChanged();
    } catch (error: any) {
      console.error('Error unlocking UC', error);
      addToast(error?.message || 'Erro ao destravar a UC.', 'error');
    } finally {
      setDestravandoId(null);
    }
  };

  return (
    <div className="mt-6 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-blue-100 dark:border-blue-900/30">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 flex items-center gap-1.5">
          <Lock size={14} /> UCs Travadas ({ucs.length})
        </h3>
        <p className="text-xs text-blue-700/70 dark:text-blue-400/70 mt-0.5">
          Não entram na distribuição deste rateio até serem destravadas por um Gestor.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-blue-800/60 dark:text-blue-400/60 uppercase">
              <th className="py-2 px-4">UC</th>
              <th className="py-2 px-4">Titular</th>
              <th className="py-2 px-4">Motivo</th>
              <th className="py-2 px-4">Travada em</th>
              <th className="py-2 px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {ucs.map(uc => (
              <tr key={uc.id} className="border-t border-blue-100/70 dark:border-blue-900/20">
                <td className="py-2 px-4 font-medium text-gray-900 dark:text-gray-100">{uc.numero_uc}</td>
                <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.nome_cliente || '—'}</td>
                <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                  {(uc.rateio_motivo_travamento && MOTIVO_BADGE[uc.rateio_motivo_travamento]) || '—'}
                </td>
                <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{formatData(uc.rateio_travado_em)}</td>
                <td className="py-2 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={e => setHistorico({ uc, anchor: e.currentTarget.getBoundingClientRect() })}
                      title="Ver Histórico"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-800"
                    >
                      <History size={14} />
                    </button>
                    {isGestor && (
                      <button
                        type="button"
                        disabled={destravandoId === uc.id}
                        onClick={() => handleDestravar(uc)}
                        title="Destravar UC"
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-primary hover:bg-white dark:hover:bg-gray-800 disabled:opacity-60"
                      >
                        {destravandoId === uc.id ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
                        Destravar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {historico && (
        <UcHistoricoPopover anchor={historico.anchor} uc={historico.uc} onClose={() => setHistorico(null)} />
      )}
    </div>
  );
};

export default UcsTravadasCard;
